import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push, replace } from 'connected-react-router';
import moment from 'moment-timezone';

import Strings from '../../Strings';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import { notificationActions } from '../../components/Notification/redux/actions';
import { openModalAction } from '../../actions/modal';
import { CREATE_ORDER_RESULT, CREATE_SHIPMENT_RESULT, EDIT_SHIPMENT_RESULT, GET_ALL_WAIVED_PENDING_RETURNS_RESULT, GET_KITS_AWAITING_BULK_RETURN_RESULT, GET_KITS_FOR_INSPECTION_RESULT, GET_PENDING_RETURNS_RESULT, shippingActions, WAIVER_PENDING_RETURN_RESULT } from './actions';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../constants';
import { TextInput } from '../../components/PageHeader/TextInput';
import { GET_MY_ORGS_RESULT, actions as orgFilterActions } from '../../components/OrgSelect/orgFilterActions';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { OrgFilter } from '../../components/AdvancedFilters/FiltersDefinition';
import { getRelevantFilters } from '../../components/AdvancedFilters/helpers';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { hasPermission, PERMISSIONS } from '../../utils/userPermissions';
import { isShipper, isSuperUser } from '../../utils/userRoles';
import { toolTipForTable } from '../../utils';
import { ReturnMethodEnum, OrderStatusEnum } from './ShippingOrders';
import { Tab, Tabs } from '../../components/Tabs';
import { ActionButton, ActionWidget } from '../../components/ActionWidget';
import SimpleModal from '../../containers/Modal/SimpleModal';
import { TextArea } from '../../containers/Form';
import { initialPendingReturnsStatus, visiblePendingReturnsTabs } from './pendingReturnsTabs';

const getDeviceIds = devices => (devices || []).map(d => d.cap_id).filter(Boolean);

const formatDevices = devices => {
  const list = getDeviceIds(devices);
  if (list.length === 0) return '-';
  return toolTipForTable(list.join(', '), list);
};

const tabs = [
  {id: 'pending-returns-patients', label: 'Patient returns', permission: PERMISSIONS.SHIPPING_PENDING_RETURNS_VIEW},
  {id: 'pending-returns-pharmacy', label: 'Pharmacy returns', permission: PERMISSIONS.SHIPPING_BULK_PENDING_RETURNS_VIEW},
  {id: 'pending-returns-waived', label: 'Waived returns', permission: PERMISSIONS.SHIPPING_PENDING_RETURNS_VIEW},
];
const kitsForInspectionTab = {id: 'pending-returns-kits_for-inspection', label: 'Kits awaiting inspection'};

export function WaiveReturnModal({ onCancel, onConfirm }) {
  const [reason, setReason] = useState('');

  return (
    <SimpleModal
      name="waive-return-modal"
      title={Strings.shipping.waiveReturn}
      onCancel={onCancel}
      onSubmit={() => onConfirm(reason)}
      isSubmitDisabled={!reason}
      content={(
        <TextArea
          label={Strings.shipping.waiveReturnReason}
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={4}
          highlightInvalid
          isRequired
        />
      )}
    />
  );
}

WaiveReturnModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

function PendingReturns(props) {
  const { loading, filters, refreshTimestamp } = props;
  const { getPendingReturns, getKitsAwaitingBulkReturn, getWaivedPendingReturns, getKitsForInspection, getOrgs, openEditShipmentModal } = props;
  const { openConfirmModal, showNotification, refreshData, replacePath } = props;
  const { createOrder, editOrder, createShipment, editShipment, waiverPendingReturn, revokePendingReturnWaiver, releaseKitFromInspection } = props;
  const urlParams = new URLSearchParams(window.location.search);
  const statusFromUrl = urlParams.get('status');

  const visibleTabs = React.useMemo(() => visiblePendingReturnsTabs(tabs, kitsForInspectionTab, {
    canSee: hasPermission,
    isPrivileged: isSuperUser() || isShipper(),
  }), []);
  const [status, setStatus] = useState(() => initialPendingReturnsStatus(statusFromUrl, visibleTabs));
  const [pendingReturns, setPendingReturns] = useState([]);
  const [waivedPendingReturns, setWaivedPendingReturns] = useState([]);
  const [filteredWaivedReturns, setFilteredWaivedReturns] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [kitsAwaitingBulkReturn, setKitsAwaitingBulkReturn] = useState([]);
  const [filteredKits, setFilteredKits] = useState([]);
  const [kitsForInspection, setKitsForInspection] = useState([]);
  const [filteredKitsForInspection, setFilteredKitsForInspection] = useState([]);
  const [selectedKits, setSelectedKits] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [showWaiveReturnModal, setShowWaiveReturnModal] = useState(false);
  const canCreateOrder = hasPermission(PERMISSIONS.SHIPPING_ORDERS_EDIT) || isShipper() || isSuperUser();
  const tableRef = useRef(null);
  const canEditPendingReturns = hasPermission(PERMISSIONS.SHIPPING_PENDING_RETURNS_EDIT) || isShipper() || isSuperUser();
  const canEditBulkPendingReturns = hasPermission(PERMISSIONS.SHIPPING_BULK_PENDING_RETURNS_EDIT) || isShipper() || isSuperUser();

  useEffect(() => {
    if (status) {
      const newUrlParams = new URLSearchParams(window.location.search);
      newUrlParams.set('status', status);
      replacePath(`${window.location.pathname}?${newUrlParams}`);
    }
  }, [status]);

  const loadData = () => {
    if (hasPermission(PERMISSIONS.SHIPPING_PENDING_RETURNS_VIEW)) {
      getPendingReturns().then(resp => {
        if (resp?.type === GET_PENDING_RETURNS_RESULT) {
          setPendingReturns(resp?.response?.data?.map(entry => ({
            ...entry.patient,
            ...entry.enrollment,
            ...entry.kit,
            org_name: orgs?.find(o => o.masked_id === entry.kit.current_org_uuid)?.name || '',
            org_uuid: entry.kit.current_org_uuid,
            suggested_return_targets: entry.suggested_return_targets,
          })) || []);
        }
      });
      getWaivedPendingReturns().then(resp => {
        if (resp?.type === GET_ALL_WAIVED_PENDING_RETURNS_RESULT) {
          setWaivedPendingReturns(resp?.response?.data?.map(entry => ({
            ...entry.patient,
            ...entry.enrollment,
            ...entry.kit,
            ...entry.waiver,
            org_name: orgs?.find(o => o.masked_id === entry.kit.current_org_uuid)?.name || '',
            org_uuid: entry.kit.current_org_uuid,
          })) || []);
        }
      });
    }
    if (hasPermission(PERMISSIONS.SHIPPING_BULK_PENDING_RETURNS_VIEW)) {
      getKitsAwaitingBulkReturn().then(resp => {
        if (resp?.type === GET_KITS_AWAITING_BULK_RETURN_RESULT) {
          setKitsAwaitingBulkReturn(resp?.response?.data?.map(kit => ({
            ...kit,
            name: kit.original_patient?.name,
            patient_uuid: kit.original_patient?.patient_uuid,
            pharmacy_patient_id: kit.original_patient?.pharmacy_patient_id,
          }))|| []);
        }
      });
    }

    if (isSuperUser() || isShipper()) {
      getKitsForInspection().then(resp => {
        if (resp?.type === GET_KITS_FOR_INSPECTION_RESULT) {
          setKitsForInspection(resp?.response?.data?.map(kit => ({
            ...kit,
            ...kit.original_patient,
          }))|| []);
        }
      });
    }
    tableRef?.current?.resetSelectedRows();
  };

  useEffect(() => {
    getOrgs().then(resp => {
      if (resp?.type === GET_MY_ORGS_RESULT) {
        setOrgs(resp?.response?.data || []);
      }
    });
  }, [getPendingReturns, refreshTimestamp]);

  useEffect(() => {
    if (orgs?.length) {
      loadData();
    }
  }, [orgs]);
  
  const filterData = data => {
    let ret = data || [];
    if (searchQuery) {
      const propertiesToSearch = [
        'name', 'patient_uuid', 'pharmacy_patient_id', 'phone', 'kit_uuid', 'kit_name', 'org_uuid', 'org_name', 'enrollment_uuid', 'program', 'device_summary'
      ];
      // Helper function to get nested property values
      const getNestedProperty = (obj, path) => {
        return path.split('.').reduce((current, key) => current?.[key], obj);
      };
      
      // Helper to search through values including arrays of objects
      const valueMatchesSearch = (value, query) => {
        const lowerQuery = query.toLowerCase();
        
        if (Array.isArray(value)) {
          // For arrays of objects, search all string properties
          return value.some(item => {
            if (typeof item === 'object' && item !== null) {
              return Object.values(item).some(v => 
                typeof v === 'string' && v.toLowerCase().includes(lowerQuery)
              );
            }
            return item.toString().toLowerCase().includes(lowerQuery);
          });
        }
        
        if (typeof value === 'string') {
          return value.toLowerCase().includes(lowerQuery);
        }
        
        return false;
      };
      
      ret = ret.filter(s => {
        return propertiesToSearch.some(prop => {
          const value = getNestedProperty(s, prop);
          return valueMatchesSearch(value, searchQuery);
        });
      });
    }
    
    const appliedFilters = getRelevantFilters(filters);
    const filtersToCheck = ['org_uuid'];
    const hasFilters = filtersToCheck.some(f => appliedFilters?.some(af => af.group === f));
    
    if (hasFilters) {
      filtersToCheck.forEach(filterGroup => {
        const filteredByGroup = appliedFilters?.filter(f => f.group === filterGroup);
        if (filteredByGroup?.length) {
          ret = ret.filter(p => filteredByGroup.some(f => f.value === p[filterGroup]));
        }
      });
    }
    return ret;
  };

  useEffect(() => {
    if (!pendingReturns?.length) {
      setFilteredReturns([]);
      return;
    }
    setFilteredReturns(filterData(pendingReturns));
  }, [pendingReturns, searchQuery, filters, status]);

  useEffect(() => {
    if (!kitsAwaitingBulkReturn?.length) {
      setFilteredKits([]);
      return;
    }
    setFilteredKits(filterData(kitsAwaitingBulkReturn));
  }, [kitsAwaitingBulkReturn, searchQuery, filters, status]);

  useEffect(() => {
    if (!waivedPendingReturns?.length) {
      setFilteredWaivedReturns([]);
      return;
    }
    setFilteredWaivedReturns(filterData(waivedPendingReturns));
  }, [waivedPendingReturns, searchQuery, filters, status]);

  useEffect(() => {
    if (!kitsForInspection?.length) {
      setFilteredKitsForInspection([]);
      return;
    }
    setFilteredKitsForInspection(filterData(kitsForInspection));
  }, [kitsForInspection, searchQuery, filters, status]);

  const markAsReturnedByPatient = row => {
    openConfirmModal({
      title: Strings.formatString(Strings.shipping.markAsReturnedByPatientQuestion, row.kit_uuid),
      onConfirmAction: async () => {
        const orderResp = await createOrder({
          flow: 'return',
          order_type: 'standard',
          patient_uuid: row.patient_uuid,
          enrollment_uuids: [row.enrollment_uuid],
          source_kit_uuid: row.kit_uuid,
          target_org_uuid: row.current_org_uuid,
        })
        const order = orderResp?.response;
        if (orderResp?.type === CREATE_ORDER_RESULT) {
          const shipmentResp = await createShipment({
            order_uuid: order.order_uuid,
            return_method: ReturnMethodEnum.pharmacy_walk_in,
            kit_ids: [row.kit_uuid],
          });
          if (shipmentResp?.type === CREATE_SHIPMENT_RESULT) {
            const deliveredResp = await editShipment(shipmentResp?.response?.shipment_uuid, {
              delivered_date: moment().format(),
              status: 'delivered',
              shipping_address: { street: '', city: '', state: '', zip: '', country: '' },
            })
            if (deliveredResp?.type !== EDIT_SHIPMENT_RESULT) {
              showNotification(deliveredResp?.data?.error?.message, 5000, true);
            }
          } else {
            // Shipment creation failed (e.g. the kit is still on an active
            // outbound shipment), so the return order we just created would
            // otherwise linger as an orphan and make a retry fail with a
            // different, confusing error. Roll it back (RXC-1349).
            await editOrder(order.order_uuid, { status: OrderStatusEnum.cancelled });
            showNotification(shipmentResp?.data?.error?.message, 5000, true);
          }
        } else {
          showNotification(orderResp?.data?.error?.message, 5000, true);
        }
      },
      hideCaution: true,
      caption: Strings.shipping.markAsReturnedByPatient,
      confirmPostAction: () => {
        showNotification(Strings.shipping.markAsReturnedByPatientSuccess, 5000, false);
        loadData();
        refreshData();
      },
    });
  };

  const createReturnShipments = (shipments) => {
    let returnTargets = shipments?.filter(s => s.suggested_return_targets)?.map(s => s.suggested_return_targets).flat();
    if (returnTargets?.length === 0) {
      returnTargets = orgs?.filter(o => o.organization_type === 'warehouse')?.map(o => ({ org_uuid: o.masked_id, name: o.name, type: 'warehouse' })) || [];
    }
    openEditShipmentModal({
      shipment: {
        kit_ids: shipments.map(s => s.kit_uuid),
        patient_uuid: status === 'pending-returns-patients' ? shipments[0].patient_uuid : undefined,
        enrollment_uuids: status === 'pending-returns-patients' ? [shipments[0].enrollment_uuid] : undefined,
        suggested_return_targets: returnTargets,
        return_method: status === 'pending-returns-patients' ? ReturnMethodEnum.mail : ReturnMethodEnum.bulk_transfer,
      },
      onSuccess: () => {loadData(); refreshData();},
      mode: 'return',
    });
  };

  const onWaiverPendingReturn = reason => {
    const selectedKit = selectedKits?.[0];
    if (!selectedKit) return;
    waiverPendingReturn(selectedKit.enrollment_uuid, { reason }).then(resp => {
      if (resp?.type === WAIVER_PENDING_RETURN_RESULT) {
        showNotification(Strings.shipping.waiveReturnSuccess, 5000, false);
        setShowWaiveReturnModal(false);
        setSelectedKits([]);
        loadData();
        refreshData();
      } else {
        showNotification(resp?.data?.error?.message, 5000, true);
      }
    });
  };

  const onRevokeWaiverReturn = row => {
    openConfirmModal({
      title: Strings.formatString(Strings.shipping.revokeWaiverReturnQuestion, row.kit_uuid),
      onConfirmAction: () => revokePendingReturnWaiver(row.enrollment_uuid),
      confirmPostAction: () => {
        showNotification(Strings.shipping.revokeWaiverReturnSuccess, 5000, false);
        loadData();
        refreshData();
      },
      hideCaution: true,
    });
  };

  const onReleaseFromInspection = row => {
    openConfirmModal({
      title: Strings.formatString(Strings.shipping.releaseFromInspectionQuestion, row.kit_uuid),
      onConfirmAction: () => releaseKitFromInspection(row.kit_uuid),
      confirmPostAction: () => {
        showNotification(Strings.shipping.releaseFromInspectionSuccess, 5000, false);
        loadData();
        refreshData();
      },
      hideCaution: true,
      caption: Strings.shipping.releaseFromInspection,
    });
  };

  const buttons = React.useMemo(() => {
    const ret = [];
    // Each action is gated by what it does: shipments and "returned by patient"
    // create a return order, waivers only touch the waiver record.
    if (canCreateOrder && (status === 'pending-returns-patients' && canEditPendingReturns || status === 'pending-returns-pharmacy' && canEditBulkPendingReturns)) {
      ret.push({
        icon: 'package',
        onClick: (_, row) => createReturnShipments([row]),
        text: Strings.shipping.createShipment,
      });
    }
    if (status === 'pending-returns-waived' && canEditPendingReturns) {
      ret.push({
        icon: 'refresh',
        onClick: (_, row) => onRevokeWaiverReturn(row),
        text: Strings.shipping.revokeWaiverReturn,
      });
    }
    if (status === 'pending-returns-patients' && canEditPendingReturns && canCreateOrder) {
      ret.push({
        icon: 'check',
        onClick: (_, row) => markAsReturnedByPatient(row),
        text: Strings.shipping.markAsReturnedByPatient,
      });
    }
    if (status === 'pending-returns-patients' && canEditPendingReturns) {
      ret.push({
        icon: 'delete',
        onClick: (_, row) => {setShowWaiveReturnModal(true); setSelectedKits([row]);},
        text: Strings.shipping.waiveReturn,
      })
    }
    if (status === 'pending-returns-kits_for-inspection' && (isShipper() || isSuperUser())) {
      ret.push({
        icon: 'check',
        onClick: (_, row) => onReleaseFromInspection(row),
        text: Strings.shipping.releaseFromInspection,
      });
    }
    return ret;
  }, [status, orgs, filters, pendingReturns, kitsAwaitingBulkReturn, canCreateOrder]);

  const columns = React.useMemo(() => {
    const ret = [];
    if (status !== 'pending-returns-kits_for-inspection') {
      ret.push(
        columnHelper.accessor('pharmacy_patient_id', {
          header: Strings.capPatient.patientId,
          size: 250,
        }),
        columnHelper.accessor('name', {
          header: Strings.capPatient.patientName,
          cell: info => info.getValue(),
          size: 200,
        }),
      );
    }
    if (status === 'pending-returns-patients' || status === 'pending-returns-waived') {
      ret.push(
        columnHelper.accessor('phone', {
          header: Strings.phoneNumber,
          cell: info => info.getValue() || '-',
          size: 200,
        }),
        columnHelper.accessor('enrollment_uuid', {
          header: Strings.capPatient.enrollment,
          cell: info => info.getValue() || '-',
          size: 200,
        }),
        columnHelper.accessor('program', {
          header: Strings.program,
          cell: info => info.getValue() || '-',
          size: 200,
        }),
        columnHelper.accessor('terminated_at', {
          header: Strings.capPatient.disenrolledDate,
          cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
          size: 200,
        }),
      );
    }
    if (status === 'pending-returns-pharmacy') {
      ret.push(columnHelper.accessor('dropped_off_at', {
        header: Strings.shipping.droppedOffDate,
        cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
        size: 250,
      }));
    }
    ret.push(columnHelper.accessor('kit_uuid', {
      header: Strings.kitId,
      cell: info => info.getValue() || '-',
      size: 200,
    }));
    if (status === 'pending-returns-kits_for-inspection') {
      ret.push(columnHelper.accessor('kit_name', {
        header: Strings.kitName,
        cell: info => info.getValue() || '-',
        size: 200,
      }));
    }
    ret.push(columnHelper.accessor('org_name', {
      header: Strings.organization,
      cell: info => info.getValue() || '-',
      size: 300,
    }));
    if (status === 'pending-returns-patients' || status === 'pending-returns-waived') {
      ret.push(
        columnHelper.accessor('device_summary', {
          header: Strings.devices,
          cell: info => formatDevices(info.row.original.device_summary),
          accessorFn: row => getDeviceIds(row.device_summary).join(', '),
          size: 300,
          enableSorting: false,
        })
      );
    } else {
      ret.push(
        columnHelper.accessor('device_count', {
          header: Strings.devices,
          size: 50,
        })
      );
    }
    if (status === 'pending-returns-waived') {
      ret.push(columnHelper.accessor('reason', {
        header: Strings.shipping.waiveReturnReason,
        size: 300,
      }));
      ret.push(columnHelper.accessor('waived_at', {
        header: Strings.shipping.waivedDate,
        cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
        size: 200,
      }));
      ret.push(columnHelper.accessor('waived_by', {
        header: Strings.shipping.waivedBy,
        cell: info => info.getValue() || '-',
      }));
    }

    if (status === 'pending-returns-kits_for-inspection') {
      ret.push(
        columnHelper.accessor('received_at', {
          header: Strings.shipping.receivedAt,
          cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
          size: 200,
        })
      );
    }

    return ret;
  }, [status, orgs]);

  const getTabRightContent = React.useCallback(() => {
    return (
      <>
        <TextInput placeholder={Strings.search} onChange={e => setSearchQuery(e.target.value)} />
        <AdvancedFilters>
          {orgs?.length > 0 && <Filter definition={OrgFilter(orgs, false, 'org_uuid', Strings.organization).org_uuid} />}
        </AdvancedFilters>
      </>
    );
  }, [status, orgs]);

  const tableForTab = React.useMemo(() => {
    switch (status) {
      case 'pending-returns-patients':
        return (
          <Table3
            isLoading={loading}
            name="pending-returns-patients"
            key="pending-returns-patients"
            data={filteredReturns || []}
            columns={columns}
            buttons={buttons}
            enableColumnFiltering
            noScroll
            pinFirstColumn
          />
        );
      case 'pending-returns-waived':
        return (
          <Table3
            isLoading={loading}
            name="pending-returns-waived"
            key="pending-returns-waived"
            data={filteredWaivedReturns || []}
            columns={columns}
            buttons={buttons}
            enableColumnFiltering
            noScroll
            pinFirstColumn
          />
        );
      case 'pending-returns-kits_for-inspection':
        return (
          <Table3
            isLoading={loading}
            name="pending-returns-kits_for-inspection"
            key="pending-returns-kits_for-inspection"
            data={filteredKitsForInspection || []}
            columns={columns}
            buttons={buttons}
            enableColumnFiltering
            noScroll
            pinFirstColumn
          />
        );
      default:
        return (
          <Table3
            isLoading={loading}
            name="pending-returns-pharmacy"
            key="pending-returns-pharmacy"
            data={filteredKits || []}
            columns={columns}
            buttons={buttons}
            enableColumnFiltering
            enableRowSelection={canEditBulkPendingReturns}
            onRowSelectionChange={canEditBulkPendingReturns ? setSelectedKits : undefined}
            noScroll
            pinFirstColumn
          />
        );
    }
  }, [status, filteredReturns, filteredWaivedReturns, filteredKitsForInspection, filteredKits, setSelectedKits, buttons, columns]);

  const tabArray = React.useMemo(() => visibleTabs.map(s => (
    <Tab
      label={s.label}
      key={s.id}
      count={
        s.id === 'pending-returns-patients'
        ? filteredReturns?.length || 0
        : s.id === 'pending-returns-waived'
        ? filteredWaivedReturns?.length || 0
        : s.id === 'pending-returns-kits_for-inspection'
        ? kitsForInspection?.length || 0
        : filteredKits?.length || 0
      }
    >
      <AdvancedFiltersBar
        customFilters={{...OrgFilter(orgs, false, 'org_uuid', Strings.organization)}}
      />
      {tableForTab}
      <ActionWidget show={status === 'pending-returns-pharmacy' && selectedKits?.length > 0}>
        <ActionButton
          img="action-package"
          tooltiptext={Strings.shipping.prepareShipments}
          text={Strings.shipping.prepareShipments}
          action={() => createReturnShipments(selectedKits)}
        />
      </ActionWidget>
      {showWaiveReturnModal && (
        <WaiveReturnModal
          onCancel={() => {setShowWaiveReturnModal(false); setSelectedKits([]);}}
          onConfirm={reason => onWaiverPendingReturn(reason)}
        />
      )}
    </Tab>
  )), [visibleTabs, tableForTab, filteredReturns, filteredKits, filteredWaivedReturns, filteredKitsForInspection, selectedKits, loading, orgs, canEditBulkPendingReturns]);

  return (
    <Tabs
      containerClassName="shipping-list-tabs"
      defaultIndex={visibleTabs.findIndex(t => t.id === status)}
      onChange={i => {
        setSelectedKits([]);
        setShowWaiveReturnModal(false);
        setStatus(visibleTabs[i].id);
      }}
      rightContent={getTabRightContent()}
    >
      {tabArray}
    </Tabs>
  );
}

PendingReturns.propTypes = {
  deleteOrder: PropTypes.func.isRequired,
  refreshTimestamp: PropTypes.any,
  showNotification: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  filters: PropTypes.array,
  orgIds: PropTypes.string,
  orgs: PropTypes.array,
  orders: PropTypes.array,
  loading: PropTypes.bool,
  onNavigate: PropTypes.func.isRequired,
  replacePath: PropTypes.func.isRequired,
  refreshData: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  getPendingReturns: () => dispatch(shippingActions.getPendingReturns()),
  waiverPendingReturn: (id, data) => dispatch(shippingActions.waiverPendingReturn(id, data)),
  revokePendingReturnWaiver: (id) => dispatch(shippingActions.revokePendingReturnWaiver(id)),
  getKitsAwaitingBulkReturn: () => dispatch(shippingActions.getKitsAwaitingBulkReturn()),
  getKitsForInspection: () => dispatch(shippingActions.getKitsForInspection()),
  releaseKitFromInspection: (id) => dispatch(shippingActions.releaseKitFromInspection(id)),
  getWaivedPendingReturns: () => dispatch(shippingActions.getAllWaivedPendingReturns()),
  createOrder: (orderData) => dispatch(shippingActions.createOrder(orderData)),
  editOrder: (id, orderData) => dispatch(shippingActions.editOrder(id, orderData)),
  createShipment: (shipmentData) => dispatch(shippingActions.createShipment(shipmentData)),
  editShipment: (id, shipmentData) => dispatch(shippingActions.editShipment(id, shipmentData)),
  getOrgs: request => dispatch(orgFilterActions.getMyOrgs(request)),
  openEditShipmentModal: data => dispatch(openModalAction('edit-shipment', data)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  onNavigate: (path, state) => dispatch(push(path, state)),
  replacePath: url => dispatch(replace(url)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PendingReturns);
